import { queryOptions } from "@tanstack/react-query"
import type {
  Group,
  GroupSummary,
  HarvestReport,
  MemberSummary,
  IdentityName,
  MergeCounts,
  OwnIdentity,
  Profile,
  Role,
  SlackUserGroup,
} from "@beacon/core/access"
import { apiBase } from "@/sign-in/client.ts"
import { Refused } from "./refused.ts"

/** The web API's refusal behind a failed call, or `unknown` for anything else — a network error. */
export function refusalOf(error: unknown) {
  return error instanceof Refused ? error.refusal : "unknown"
}

/** One call to the web API with the session cookie, giving back its JSON or throwing `Refused`. */
export async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    method,
    credentials: "include",
    ...(body !== undefined && {
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    }),
  })
  if (!response.ok) {
    const answer: unknown = await response.json().catch(() => ({}))
    const { error } = answer as { error?: string }
    throw new Refused(error ?? "unknown", answer)
  }
  return (response.status === 204 ? undefined : await response.json()) as T
}

export const listMembers = () => call<MemberSummary[]>("GET", "/members")

export const setRole = (memberId: string, role: Role) =>
  call<void>("PUT", `/members/${memberId}/role`, { role })

export const admitWorkspace = () => call<HarvestReport>("POST", "/members/admit-workspace")

/** Ends a Member's access, keeping their history; an Admin's alone. */
export const deactivateMember = (memberId: string) =>
  call<void>("POST", `/members/${memberId}/deactivate`)

/** Pairs of active Members who share a name, for an Admin to merge or leave. */
export const mergeSuggestionsQuery = queryOptions({
  queryKey: ["members", "merge-suggestions"],
  queryFn: () => call<{ first: string; second: string }[]>("GET", "/members/merge-suggestions"),
})

/** What merging `mergedId` into `keptId` would move, counted by kind. */
export const mergePreviewQuery = (keptId: string, mergedId: string) =>
  queryOptions({
    queryKey: ["members", "merge-preview", keptId, mergedId],
    queryFn: () => call<MergeCounts>("GET", `/members/${keptId}/merge/${mergedId}`),
  })

/** Merges `mergedId` into `keptId`, answering what moved. */
export const mergeMembers = (keptId: string, mergedId: string) =>
  call<MergeCounts>("POST", `/members/${keptId}/merge/${mergedId}`)

/** Adds a Member by name and at least one email, mobile number or username. */
export const addMember = (member: {
  firstName: string
  lastName: string
  identities: IdentityName[]
}) => call<{ id: string }>("POST", "/members", member)

/** Edits another Member's Profile, as an Admin. */
export const editMemberProfile = (memberId: string, edit: Partial<Profile>) =>
  call<void>("PUT", `/members/${memberId}/profile`, edit)

/** Every Identity a Member has, as an Admin manages them. */
export const memberIdentitiesQuery = (memberId: string) =>
  queryOptions({
    queryKey: ["members", memberId, "identities"],
    queryFn: () => call<OwnIdentity[]>("GET", `/members/${memberId}/identities`),
  })

/** Gives a Member an Identity with no code. */
export const attachIdentity = (memberId: string, identity: IdentityName) =>
  call<OwnIdentity>("POST", `/members/${memberId}/identities`, identity)

/** Takes an Identity away from a Member. */
export const detachIdentity = (memberId: string, identityId: string) =>
  call<void>("DELETE", `/members/${memberId}/identities/${identityId}`)

/**
 * Invites a Member to set a password: emailed to them, or a setup link to pass on to one with no
 * email. A new one replaces the last.
 */
export const issueInvitation = (memberId: string) =>
  call<{ emailed: true } | { emailed: false; link: string }>(
    "POST",
    `/members/${memberId}/invitation`,
  )

/** Brings back a deactivated Member's access. */
export const reactivateMember = (memberId: string) =>
  call<void>("POST", `/members/${memberId}/reactivate`)

export const listGroups = () => call<GroupSummary[]>("GET", "/groups")

export const createGroup = (name: string) => call<Group>("POST", "/groups", { name })

export const renameGroup = (groupId: string, name: string) =>
  call<void>("PATCH", `/groups/${groupId}`, { name })

export const deleteGroup = (groupId: string) => call<void>("DELETE", `/groups/${groupId}`)

export const addToGroup = (groupId: string, memberId: string) =>
  call<void>("PUT", `/groups/${groupId}/members/${memberId}`)

export const removeFromGroup = (groupId: string, memberId: string) =>
  call<void>("DELETE", `/groups/${groupId}/members/${memberId}`)

export const listSlackUserGroups = () => call<SlackUserGroup[]>("GET", "/slack-user-groups")

export const importSlackUserGroup = (groupId: string, slackUserGroupId: string) =>
  call<{ added: number; joined: number; leftOut: number }>("POST", `/groups/${groupId}/import`, {
    slackUserGroupId,
  })

/** Every Member, for an Admin: the Members page, and the Groups page's picker. */
export const membersQuery = queryOptions({ queryKey: ["members"], queryFn: listMembers })

/** Every group with its Members, for an Admin. */
export const groupsQuery = queryOptions({ queryKey: ["groups"], queryFn: listGroups })

/** Slack's user groups, which a group may be filled from once. */
export const slackUserGroupsQuery = queryOptions({
  queryKey: ["slack-user-groups"],
  queryFn: listSlackUserGroups,
})
